/**
 * =========================================================================================
 *     Copyright © 2025 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   Warehouses.tsx
 *     Description     :   Warehouses secondary dashboard page
 *     Author          :   Peter Dobler
 *     Created         :   2026-02-02
 *     Modified        :   2026-02-02 | Peter Dobler | Initial warehouse list page
 *     Modified        :   2026-06-14 | Conformed to the Spec 197 design blueprint —
 *                         migrated off MUI (Container/Paper/Table/Chip/Grid/TextField/
 *                         Tabs) onto the canonical Tailwind+token UI kit (PageLayout,
 *                         Card, DataTable, StatCard, Input, Select, Button, Badge,
 *                         ErrorState/EmptyState/LoadingState). The warehouse-detail
 *                         draggable MUI <Dialog> is retained per blueprint §7 (complex
 *                         themed widget) — data fetching / logic unchanged.
 *     Constitution    :   v1.19.0
 *     Feature         :   warehouse-management
 * =========================================================================================
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { useHasAccess } from '../../../hooks/useAccess';
import {
  Search,
  AlertTriangle,
  CheckCircle2,
  Database,
  ChevronRight,
  Filter,
  Inbox,
} from 'lucide-react';
import { clsx } from 'clsx';
import api from '../services/api';
import WarehouseDetailPage from './WarehouseDetailPage';
import WarehouseCatalog from './WarehouseCatalog';
import { formatWarehouseSize, unmeasuredSizeNote, warehouseSizeNote } from '../utils/entitySize';
import { warehouseHelp } from '../../../data/help/warehouseHelp';
import {
  PageLayout,
  Card,
  DataTable,
  Button,
  Input,
  Select,
  Badge,
  StatCard,
  EmptyState,
  ErrorState,
  LoadingState,
  Extended,
  QuickGlance,
  AppTooltip,
  PageHelpPanel,
  Modal,
} from '../../../components/ui';
import type { DataTableColumn } from '../../../components/ui';
import { formatEastern } from '@/utils/dateFormatter';
import { warehouseFieldTooltips } from '../help/warehouseDetailHelp';

// ============================================================================
// Types
// ============================================================================

export interface Warehouse {
  id: string;
  fabric_id: string;
  workspace_id: string;
  workspace_name: string;
  display_name: string;
  description: string | null;
  warehouse_type: string | null;
  status: string;
  is_active: boolean;
  capacity_id: string | null;
  capacity_name: string | null;
  capacity_type: number | null;
  storage_size_gb: number | null;
  size_measurement_error: string | null;
  table_count: number | null;
  protection_tier: string | null;
  created_at: string;
  updated_at: string;
  discovered_at: string | null;
  last_seen_at: string | null;
  /** Spec 286 T159 item 6: last_seen_at is written by no collector; this is when collection last recorded a change. */
  last_changed_at: string | null;
  /** Spec 286 T159 item 27: when Fabric last updated the warehouse (Admin - Items - List Items lastUpdatedDate). */
  fabric_last_updated_at: string | null;
}

interface WarehouseStats {
  total: number;
  active: number;
  inactive: number;
  totalSizeGb?: number;
}

interface Pagination {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

type StatusFilter = 'all' | 'active' | 'inactive';

// ============================================================================
// Component
// ============================================================================

/** One empty list, so memos keyed on the rows stay stable before the first load. */
const NO_ROWS: Warehouse[] = [];

const Warehouses: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // State
  const [selectedWarehouseId, setSelectedWarehouseId] = useState<string | null>(null);

  // Spec 216 T029: the Warehouse Catalog is now the "Protection" tab of this
  // list (/warehouses?view=protection; the old /warehouses/catalog redirects).
  const [view, setView] = useState<'list' | 'protection'>(
    searchParams.get('view') === 'protection' ? 'protection' : 'list'
  );
  // Spec 285: the Protection view is backup-licensed (owner: gate ALL backup functionality);
  // without the license a ?view=protection deep link falls back to the list.
  const canSeeBackup = useHasAccess('backup');
  const effectiveView: 'list' | 'protection' = canSeeBackup ? view : 'list';

  // Filters
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(
    (searchParams.get('status') as StatusFilter) || 'all'
  );
  const [typeFilter, setTypeFilter] = useState(searchParams.get('type') || '');
  const workspaceFilter = searchParams.get('workspace_id') || '';

  // Spec 226 (FR-011): React Query keeps the rows of the current filters on screen through a refresh,
  // and a failed refresh shows the Refresh badge instead of replacing them. The full error view is for
  // a list that never loaded under these filters.
  const listQuery = useQuery({
    queryKey: ['warehouses', 'list', searchQuery, statusFilter, typeFilter, workspaceFilter],
    queryFn: async () => {
      const params = new URLSearchParams();
      // Small dataset: fetch ALL rows so the DataTable paginates the full set client-side.
      // The old server-side page of 20 was never navigable (the table only client-paginates
      // the rows it's handed), so the list silently showed just the first page.
      params.set('pageSize', '1000');

      if (searchQuery) params.set('search', searchQuery);
      if (statusFilter !== 'all') params.set('is_active', (statusFilter === 'active').toString());
      if (typeFilter) params.set('warehouse_type', typeFilter);
      if (workspaceFilter) params.set('workspace_id', workspaceFilter);

      return api.get<{
        success: boolean;
        data: Warehouse[];
        pagination: Pagination;
      }>(`/api/v1/warehouses?${params.toString()}`);
    },
  });
  const statsQuery = useQuery({
    queryKey: ['warehouses', 'stats'],
    queryFn: async () => (await api.get<{ success: boolean; data: WarehouseStats; }>('/api/v1/warehouses/stats')).data,
  });
  const warehouses = listQuery.data?.data ?? NO_ROWS;
  const stats = statsQuery.data ?? null;
  const loading = listQuery.isLoading;
  const error = listQuery.data || !listQuery.error
    ? null
    : listQuery.error instanceof Error ? listQuery.error.message : 'Failed to fetch warehouses';

  // Get workspace name from the first warehouse if filtering by workspace
  const filteredWorkspaceName = useMemo(() => {
    if (workspaceFilter && warehouses.length > 0) {
      return warehouses[0]?.workspace_name || 'Selected Workspace';
    }
    return null;
  }, [workspaceFilter, warehouses]);

  // Spec 226: reloads after an action or from Retry. The queries load on their own when the
  // filters change; the header's standard Refresh re-fetches both (refreshAll).
  const { refetch: refetchList } = listQuery;
  const { refetch: refetchStats } = statsQuery;
  const refreshAll = useCallback(async () => {
    await Promise.all([refetchList({ throwOnError: true }), refetchStats({ throwOnError: true })]);
  }, [refetchList, refetchStats]);

  // Update URL params when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (searchQuery) params.set('search', searchQuery);
    if (statusFilter !== 'all') params.set('status', statusFilter);
    if (typeFilter) params.set('type', typeFilter);
    if (workspaceFilter) params.set('workspace_id', workspaceFilter);
    if (effectiveView === 'protection') params.set('view', 'protection');
    setSearchParams(params);
  }, [searchQuery, statusFilter, typeFilter, workspaceFilter, effectiveView, setSearchParams]);

  // Status is applied server-side; the list shows exactly what the API returned.
  const filteredWarehouses = warehouses;

  // Total backup size across the loaded warehouses
  // Spec 286 T159 item 27: the types the warehouses carry ("Warehouse" on all 27 on dev, 2026-10-22). The fixed
  // "Standard"/"Serverless" options matched none, so either choice emptied the list. The chosen type stays offered
  // while the list is filtered to it.
  const typeOptions = useMemo(() => {
    const types = new Set(warehouses.map((w) => w.warehouse_type).filter((t): t is string => !!t));
    if (typeFilter) types.add(typeFilter);
    return [{ value: '', label: 'All Types' }, ...[...types].sort().map((t) => ({ value: t, label: t }))];
  }, [warehouses, typeFilter]);

  const totalSizeGb = useMemo(
    () => warehouses.reduce((sum, w) => sum + (Number(w.storage_size_gb) || 0), 0),
    [warehouses]
  );
  // Spec 286 T159 item 32: how many the last collection did not size; the total counts their last known size.
  const notMeasuredCount = useMemo(() => warehouses.filter((w) => w.size_measurement_error).length, [warehouses]);

  // Summary stats
  const summaryStats = useMemo(() => {
    return {
      total: stats?.total || 0,
      active: stats?.active || 0,
      inactive: stats?.inactive || 0,
    };
  }, [stats]);

  // Format date
  const formatDate = (dateString: string | null): string => {
    if (!dateString) return 'Never';
    return formatEastern(dateString);
  };

  // Format size in GB (or TB for large values)
  const formatSize = (gb: number | null | undefined): string => {
    const n = Number(gb) || 0;
    if (n <= 0) return '—';
    if (n >= 1024) return `${(n / 1024).toFixed(2)} TB`;
    return `${n.toFixed(2)} GB`;
  };

  // ── Table columns ─────────────────────────────────────────────────────────
  const columns: DataTableColumn<Warehouse>[] = [
    {
      key: 'display_name',
      header: 'Warehouse',
      width: '30%',
      render: (w) => (
        <div className="flex min-w-0 items-center gap-2">
          <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded bg-[var(--color-bg-subtle)]">
            <Database
              className={clsx(
                'h-4 w-4',
                w.is_active ? 'text-[var(--color-accent)]' : 'text-[var(--color-text-secondary)]',
              )}
            />
          </div>
          <div className="min-w-0">
            <div className="truncate font-medium text-[var(--color-text-primary)]" title={w.display_name}>
              {w.display_name}
            </div>
            {w.description && (
              <div className="truncate text-xs text-[var(--color-text-secondary)]" title={w.description}>
                {w.description}
              </div>
            )}
          </div>
        </div>
      ),
    },
    {
      key: 'workspace_name',
      header: 'Workspace',
      width: '20%',
      render: (w) => (
        <span className="text-[var(--color-text-primary)]">{w.workspace_name}</span>
      ),
    },
    {
      key: 'warehouse_type',
      header: 'Type',
      width: '12%',
      render: (w) => (
        <Badge variant="secondary" isOutlined>
          {w.warehouse_type || '—'}
        </Badge>
      ),
    },
    {
      key: 'storage_size_gb',
      header: 'Size',
      width: '11%',
      align: 'right',
      render: (w) => {
        const size = formatWarehouseSize(w.storage_size_gb, w.table_count, w.size_measurement_error);
        if (size.state === 'sized' || size.state === 'measured-tiny') {
          return (
            <AppTooltip
              title={
                size.state === 'measured-tiny'
                  ? 'Measured — real footprint under 0.01 GB'
                  : `${w.table_count ?? 0} table${(w.table_count ?? 0) === 1 ? '' : 's'}`
              }
            >
              <span className="tabular-nums text-[var(--color-text-primary)]">{size.text}</span>
            </AppTooltip>
          );
        }
        if (size.state === 'empty') {
          return (
            <AppTooltip title="No tables — this warehouse holds no data">
              <span className="text-[var(--color-text-secondary)]">Empty</span>
            </AppTooltip>
          );
        }
        // not-measured
        return (
          <AppTooltip title={warehouseSizeNote(size)}>
            <span className="italic text-[var(--color-status-warning)]">Not measured</span>
          </AppTooltip>
        );
      },
    },
    {
      key: 'status',
      header: 'Status',
      width: '12%',
      render: (w) => (
        <AppTooltip
          title={w.is_active ? 'Warehouse is currently active' : 'Warehouse is currently inactive'}
        >
          <Badge
            variant={w.is_active ? 'success' : 'secondary'}
            leftIcon={w.is_active ? <CheckCircle2 className="h-3 w-3" /> : undefined}
          >
            {w.is_active ? 'Active' : 'Inactive'}
          </Badge>
        </AppTooltip>
      ),
    },
    {
      key: 'capacity_name',
      header: 'Capacity',
      width: '16%',
      render: (w) =>
        w.capacity_name ? (
          <span className="truncate text-[var(--color-text-primary)]">{w.capacity_name}</span>
        ) : (
          <span className="text-[var(--color-text-secondary)]">—</span>
        ),
    },
    {
      // Spec 286 T159 item 6: last_seen_at is written by no collector, so this read "Never" for every warehouse.
      // Spec 286 T159 item 27: last_changed_at, the merge's own stamp, then read the same moment on all 27 warehouses
      // (one merge re-stamped every row), so the column shows when Fabric last updated the warehouse instead.
      key: 'fabric_last_updated_at',
      header: (
        <AppTooltip title={warehouseFieldTooltips.fabricLastUpdatedAt}>
          <span>Changed in Fabric</span>
        </AppTooltip>
      ),
      width: '12%',
      render: (w) => (
        <span className="text-[var(--color-text-secondary)]">
          {w.fabric_last_updated_at ? formatEastern(w.fabric_last_updated_at) : 'Not collected'}
        </span>
      ),
    },
    {
      key: 'created_at',
      header: 'Created',
      width: '10%',
      render: (w) => (
        <span className="text-[var(--color-text-secondary)]">{formatDate(w.created_at)}</span>
      ),
    },
  ];

  return (
    <PageLayout
      title="Warehouses"
      description="Manage and monitor Fabric SQL warehouses"
      icon={<Database className="h-6 w-6" />}
      onRefresh={refreshAll}
      isRefreshing={listQuery.isFetching || statsQuery.isFetching}
      lastFetchedAt={listQuery.dataUpdatedAt}
      refreshError={listQuery.error ?? statsQuery.error}
      actions={
        <>
          <PageHelpPanel content={warehouseHelp} buttonSize="sm" />
        </>
      }
    >
      {/* Breadcrumb */}
      <div className="mb-4 flex items-center gap-1 text-sm text-[var(--color-text-secondary)]">
        <Link to="/workspaces" className="hover:text-[var(--color-text-primary)] hover:underline">
          Workspaces
        </Link>
        <ChevronRight className="h-4 w-4" />
        <span className="text-[var(--color-text-primary)]">Warehouses</span>
      </div>

      {/* View switcher: operational list vs the protection catalog (T029). Spec 285: shown
          only with the backup license — a switcher with one view is not shown. */}
      {canSeeBackup && (
      <div className="mb-6 flex gap-1 border-b border-[var(--color-border)]">
        {([
          { key: 'list', label: 'List' },
          { key: 'protection', label: 'Protection' },
        ] as const).map((v) => (
          <button
            key={v.key}
            onClick={() => setView(v.key)}
            className={
              '-mb-px border-b-2 px-4 py-2 text-sm font-medium transition-colors ' +
              (view === v.key
                ? 'border-[var(--color-accent)] text-[var(--color-accent)]'
                : 'border-transparent text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]')
            }
          >
            {v.label}
          </button>
        ))}
      </div>
      )}

      {effectiveView === 'protection' ? (
        <WarehouseCatalog embedded />
      ) : error && !loading ? (
        <ErrorState
          title="Failed to load warehouses"
          message={error}
          onRetry={refreshAll}
        />
      ) : (
        <>
          {/* Workspace filter indicator */}
          {workspaceFilter && (
            <Card className="mb-6 flex items-center justify-between gap-3 border-[var(--color-status-info)]/40 bg-[var(--color-status-info)]/5 p-3">
              <div className="flex items-center gap-2 text-sm text-[var(--color-text-primary)]">
                <Filter className="h-4 w-4 text-[var(--color-status-info)]" />
                <span>
                  Showing warehouses filtered by workspace:{' '}
                  <strong>{filteredWorkspaceName || workspaceFilter}</strong>
                </span>
              </div>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => navigate('/warehouses')}
              >
                Clear Filter
              </Button>
            </Card>
          )}

          {/* Summary tiles */}
          <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
            <StatCard label="Total Warehouses" value={summaryStats.total} />
            <StatCard
              label="Active"
              value={summaryStats.active}
              valueClassName="text-[var(--color-status-success)]"
              icon={<CheckCircle2 className="h-4 w-4" />}
            />
            <StatCard
              label="Inactive"
              value={summaryStats.inactive}
              valueClassName={
                summaryStats.inactive > 0
                  ? 'text-[var(--color-status-warning)]'
                  : 'text-[var(--color-text-secondary)]'
              }
              icon={summaryStats.inactive > 0 ? <AlertTriangle className="h-4 w-4" /> : undefined}
            />
            <StatCard
              label="Total Size"
              value={formatSize(totalSizeGb)}
              subtext={unmeasuredSizeNote(notMeasuredCount)}
              valueClassName="text-[var(--color-accent)]"
              icon={<Database className="h-4 w-4" />}
            />
          </div>

          <QuickGlance>
            <Card className="p-4 text-sm text-[var(--color-text-secondary)]">
              Showing the at-a-glance summary. Open this page on a larger screen for the filters and full table.
            </Card>
          </QuickGlance>

          <Extended>
          {/* Filters */}
          <Card className="mb-6 p-4">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-12 md:items-end">
              <div className="md:col-span-4">
                <Input
                  isFullWidth
                  placeholder="Search warehouses..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  leftAddon={<Search className="h-4 w-4" />}
                />
              </div>
              <div className="md:col-span-2">
                <Select
                  isFullWidth
                  label="Status"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
                  options={[
                    { value: 'all', label: 'All Statuses' },
                    { value: 'active', label: 'Active' },
                    { value: 'inactive', label: 'Inactive' },
                  ]}
                />
              </div>
              <div className="md:col-span-2">
                <Select
                  isFullWidth
                  label="Type"
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  options={typeOptions}
                />
              </div>
              <div className="md:col-span-1">
                <Button
                  variant="ghost"
                  onClick={() => {
                    setSearchQuery('');
                    setStatusFilter('all');
                    setTypeFilter('');
                    navigate('/warehouses');
                  }}
                  disabled={!searchQuery && statusFilter === 'all' && !typeFilter && !workspaceFilter}
                >
                  Clear Filters
                </Button>
              </div>
              <div className="text-sm text-[var(--color-text-secondary)] md:col-span-2">
                {workspaceFilter
                  ? `${filteredWarehouses.length} warehouses in workspace`
                  : `${filteredWarehouses.length} of ${summaryStats.total} warehouses`}
              </div>
            </div>
          </Card>

          {/* Table */}
          <Card padding="none">
            {loading ? (
              <div className="p-4">
                <LoadingState rows={6} />
              </div>
            ) : filteredWarehouses.length === 0 ? (
              <EmptyState
                icon={<Inbox className="h-10 w-10" />}
                title="No warehouses found"
                message="Discover warehouses from Fabric workspaces to get started."
              />
            ) : (
              <DataTable
                columns={columns}
                rows={filteredWarehouses}
                rowKey={(w) => w.id}
                onRowClick={(w) => setSelectedWarehouseId(w.id)}
                emptyMessage="No warehouses found"
                minWidth="52rem"
              />
            )}
          </Card>
          </Extended>
        </>
      )}

      {/* Warehouse detail as the canonical kit Modal (Spec 197 design blueprint):
          transparent-but-blocking backdrop, framed, movable, no outside-close;
          mirrors the lakehouse detail modal for consistency. */}
      <Modal
        isOpen={!!selectedWarehouseId}
        onClose={() => setSelectedWarehouseId(null)}
        title="Warehouse Detail"
        size="xl"
      >
        {selectedWarehouseId && (
          <WarehouseDetailPage embeddedId={selectedWarehouseId} onClose={() => setSelectedWarehouseId(null)} />
        )}
      </Modal>
    </PageLayout>
  );
};

export default Warehouses;
